import { Activity, Stethoscope, Wind, Wrench, Calculator, Headset, Utensils, Microscope, ClipboardList, Beaker, Pill, MessageSquareWarning, ScanLine, Scissors, HeartHandshake, HeartPulse, ShieldCheck, ConciergeBell, Droplet, Lock } from "lucide-react";

/* ---- Inline SVG illustrations for the engine simulators (match house style) ---- */

const ConflictArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Difficult conversation">
    <defs>
      <linearGradient id="confBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#2e1065" />
        <stop offset="1" stopColor="#5b21b6" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#confBg)" />
    {/* anger burst marks over the left (client) bubble */}
    <g stroke="#fbbf24" strokeWidth="3" strokeLinecap="round">
      <line x1="40" y1="26" x2="46" y2="38" />
      <line x1="66" y1="20" x2="68" y2="34" />
      <line x1="92" y1="26" x2="86" y2="38" />
    </g>
    {/* left bubble — heated client */}
    <path d="M28 46 h104 a12 12 0 0 1 12 12 v34 a12 12 0 0 1 -12 12 h-70 l-16 16 v-16 h-18 a12 12 0 0 1 -12 -12 v-34 a12 12 0 0 1 12 -12 z" fill="#fb7185" />
    <line x1="44" y1="64" x2="120" y2="64" stroke="#7f1d1d" strokeWidth="4" strokeLinecap="round" />
    <line x1="44" y1="76" x2="104" y2="76" stroke="#7f1d1d" strokeWidth="4" strokeLinecap="round" opacity="0.7" />
    <text x="50" y="98" fill="#7f1d1d" fontFamily="monospace" fontSize="15" fontWeight="700">!!!</text>
    {/* right bubble — calm nurse */}
    <path d="M180 92 h104 a12 12 0 0 1 12 12 v30 a12 12 0 0 1 -12 12 h-18 v14 l-16 -14 h-70 a12 12 0 0 1 -12 -12 v-30 a12 12 0 0 1 12 -12 z" fill="#ecfeff" />
    <circle cx="206" cy="120" r="4" fill="#0d9488" />
    <circle cx="226" cy="120" r="4" fill="#0d9488" />
    <circle cx="246" cy="120" r="4" fill="#0d9488" />
    <text x="160" y="172" fill="#ddd6fe" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">CERT IV · DIFFICULT CONVERSATIONS</text>
  </svg>
);

const DispensingArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Dispensing medication">
    <defs>
      <linearGradient id="dispBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#083344" />
        <stop offset="1" stopColor="#0e7490" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#dispBg)" />
    {/* medicine bottle with label */}
    <rect x="36" y="62" width="74" height="92" rx="8" fill="#0e3a4a" stroke="#5eead4" strokeWidth="2" />
    <rect x="52" y="44" width="42" height="22" rx="4" fill="#155e63" stroke="#5eead4" strokeWidth="2" />
    <rect x="46" y="86" width="54" height="58" rx="4" fill="#ecfeff" />
    <line x1="54" y1="98" x2="92" y2="98" stroke="#0e7490" strokeWidth="3" strokeLinecap="round" />
    <line x1="54" y1="110" x2="92" y2="110" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="54" y1="120" x2="84" y2="120" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="54" y1="130" x2="88" y2="130" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" />
    {/* blister pack */}
    <rect x="138" y="70" width="150" height="46" rx="6" fill="#cbd5e1" />
    {Array.from({ length: 10 }).map((_, i) => (
      <circle key={`bli-${i}`} cx={156 + (i % 5) * 30} cy={84 + Math.floor(i / 5) * 22} r="8" fill="#5eead4" stroke="#0e7490" strokeWidth="1.5" />
    ))}
    {/* loose tablets */}
    <circle cx="160" cy="138" r="11" fill="#ecfeff" stroke="#5eead4" strokeWidth="2" />
    <line x1="160" y1="129" x2="160" y2="147" stroke="#0e7490" strokeWidth="1.5" />
    <circle cx="186" cy="146" r="9" fill="#ecfeff" stroke="#5eead4" strokeWidth="2" />
    <text x="244" y="146" fill="#a5f3fc" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">Rx</text>
    <text x="220" y="172" fill="#a5f3fc" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">CERT IV · DISPENSING</text>
  </svg>
);

const RadiographyArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Radiograph on viewer">
    <defs>
      <linearGradient id="radBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0f172a" />
        <stop offset="1" stopColor="#334155" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#radBg)" />
    {/* light box film */}
    <rect x="28" y="24" width="170" height="132" rx="6" fill="#0b1220" stroke="#64748b" strokeWidth="2" />
    <rect x="36" y="32" width="154" height="116" rx="4" fill="#1e293b" />
    {/* spine + ribcage */}
    <line x1="60" y1="44" x2="170" y2="44" stroke="#cbd5e1" strokeWidth="5" strokeLinecap="round" opacity="0.85" />
    {Array.from({ length: 6 }).map((_, i) => (
      <g key={`rib-${i}`} opacity="0.8">
        <path d={`M112 ${52 + i * 15} q-40 6 -50 30`} fill="none" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />
        <path d={`M112 ${52 + i * 15} q40 6 50 30`} fill="none" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />
      </g>
    ))}
    <ellipse cx="100" cy="118" rx="22" ry="18" fill="#475569" opacity="0.6" />
    {/* radiation trefoil */}
    <g transform="translate(252 70)">
      <circle r="10" fill="none" stroke="#fde047" strokeWidth="3" />
      <circle r="4" fill="#fde047" />
      {[0, 120, 240].map((a) => (
        <path key={a} d="M0 0 L14 -8 A16 16 0 0 1 14 8 Z" fill="#fde047" transform={`rotate(${a})`} />
      ))}
    </g>
    <text x="252" y="110" fill="#fde047" fontFamily="monospace" fontSize="9" fontWeight="700" textAnchor="middle">ALARA</text>
    <text x="252" y="150" fill="#cbd5e1" fontFamily="monospace" fontSize="9" fontWeight="700" textAnchor="middle">CERT IV · RADIOGRAPHY</text>
    <text x="44" y="142" fill="#64748b" fontFamily="monospace" fontSize="9">R</text>
  </svg>
);

const AsepsisArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Sterile surgical tray">
    <defs>
      <linearGradient id="aseBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#042f2e" />
        <stop offset="1" stopColor="#0f766e" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#aseBg)" />
    {/* sterile tray */}
    <rect x="30" y="58" width="260" height="84" rx="10" fill="#134e4a" stroke="#5eead4" strokeWidth="2" />
    <rect x="40" y="66" width="240" height="68" rx="6" fill="#0b3b38" />
    {/* scalpel */}
    <rect x="56" y="92" width="70" height="7" rx="3" fill="#cbd5e1" transform="rotate(-8 90 95)" />
    <path d="M120 86 l22 4 l-20 8 z" fill="#e2e8f0" transform="rotate(-8 130 92)" />
    {/* scissors */}
    <g stroke="#cbd5e1" strokeWidth="4" fill="none" transform="translate(150 78)">
      <line x1="0" y1="0" x2="44" y2="40" />
      <line x1="44" y1="0" x2="0" y2="40" />
      <circle cx="2" cy="2" r="5" />
      <circle cx="42" cy="2" r="5" />
    </g>
    {/* forceps */}
    <path d="M232 80 q14 24 4 56 M250 80 q-14 24 -4 56" fill="none" stroke="#cbd5e1" strokeWidth="4" strokeLinecap="round" />
    {/* autoclave indicator strip — passed */}
    <rect x="40" y="120" width="40" height="10" rx="2" fill="#1e293b" />
    <rect x="42" y="122" width="36" height="6" rx="1" fill="#34d399" />
    <text x="160" y="166" fill="#99f6e4" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">CERT IV · STERILE FIELD</text>
  </svg>
);

const GriefArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Bereavement support">
    <defs>
      <linearGradient id="griefBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#3b0764" />
        <stop offset="1" stopColor="#7e22ce" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#griefBg)" />
    {/* candle */}
    <rect x="60" y="78" width="26" height="66" rx="5" fill="#ede9fe" />
    <rect x="70" y="64" width="6" height="16" fill="#a78bfa" />
    <path d="M73 40 c10 12 8 24 0 30 c-8 -6 -10 -18 0 -30 z" fill="#fbbf24" />
    <path d="M73 48 c5 7 4 13 0 16 c-4 -3 -5 -9 0 -16 z" fill="#fff7ed" />
    {/* paw print */}
    <g fill="#ede9fe" transform="translate(186 70)">
      <ellipse cx="0" cy="22" rx="20" ry="16" />
      <ellipse cx="-22" cy="2" rx="8" ry="11" />
      <ellipse cx="-7" cy="-12" rx="8" ry="11" />
      <ellipse cx="11" cy="-12" rx="8" ry="11" />
      <ellipse cx="26" cy="2" rx="8" ry="11" />
    </g>
    {/* heart */}
    <path d="M250 118 c-10 -14 -30 -4 -30 10 c0 14 22 24 30 30 c8 -6 30 -16 30 -30 c0 -14 -20 -24 -30 -10 z" fill="#f9a8d4" />
    <text x="160" y="166" fill="#e9d5ff" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">CERT IV · BEREAVEMENT SUPPORT</text>
  </svg>
);

const EmergencyArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Emergency CPR">
    <defs>
      <linearGradient id="emgBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#450a0a" />
        <stop offset="1" stopColor="#b91c1c" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#emgBg)" />
    {/* ECG that arrests then a QRS spike */}
    <path d="M0 96 H70 l10 0 l8 -40 l10 70 l8 -30 H150 H320" fill="none" stroke="#fef08a" strokeWidth="2.5" strokeLinejoin="round" opacity="0.85" />
    {/* heart with cross */}
    <path d="M232 64 c-14 -18 -42 -4 -42 16 c0 22 30 36 42 46 c12 -10 42 -24 42 -46 c0 -20 -28 -34 -42 -16 z" fill="#fecaca" />
    <rect x="224" y="92" width="16" height="6" rx="2" fill="#b91c1c" />
    <rect x="229" y="87" width="6" height="16" rx="2" fill="#b91c1c" />
    {/* compression arrows */}
    <g stroke="#fff7ed" strokeWidth="3" strokeLinecap="round" fill="none">
      <path d="M232 30 v14 M226 38 l6 8 l6 -8" />
    </g>
    <text x="160" y="150" fill="#fde68a" fontFamily="monospace" fontSize="13" fontWeight="700" textAnchor="middle">CPR · 100–120 / min</text>
    <text x="160" y="170" fill="#fecaca" fontFamily="monospace" fontSize="10" fontWeight="700" textAnchor="middle">CERT IV · EMERGENCY &amp; CPR</text>
  </svg>
);

const InfectionArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Infection control">
    <defs>
      <linearGradient id="infBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#451a03" />
        <stop offset="1" stopColor="#a16207" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#infBg)" />
    {/* shield */}
    <path d="M86 30 l52 16 v40 c0 36 -28 52 -52 62 c-24 -10 -52 -26 -52 -62 v-40 z" fill="#1c1917" stroke="#fde047" strokeWidth="2.5" />
    {/* biohazard trefoil inside shield */}
    <g transform="translate(86 92)" fill="none" stroke="#fde047" strokeWidth="3.5">
      {[30, 150, 270].map((a) => (
        <g key={a} transform={`rotate(${a})`}>
          <circle cx="0" cy="-22" r="11" />
        </g>
      ))}
      <circle cx="0" cy="0" r="8" />
    </g>
    {/* isolation door sign */}
    <rect x="186" y="44" width="104" height="96" rx="8" fill="#fef3c7" stroke="#fbbf24" strokeWidth="2" />
    <rect x="186" y="44" width="104" height="24" rx="8" fill="#dc2626" />
    <text x="238" y="61" fill="#fff" fontFamily="monospace" fontSize="12" fontWeight="700" textAnchor="middle">ISOLATION</text>
    {/* glove + mask glyphs */}
    <path d="M204 84 v34 h12 v-20 l4 6 v14 h10 v-30 a6 6 0 0 0 -12 0 v6 l-4 -8 a6 6 0 0 0 -10 4 z" fill="#0ea5a4" opacity="0.9" />
    <ellipse cx="258" cy="104" rx="22" ry="13" fill="#38bdf8" />
    <line x1="236" y1="100" x2="246" y2="96" stroke="#38bdf8" strokeWidth="3" />
    <line x1="280" y1="100" x2="270" y2="96" stroke="#38bdf8" strokeWidth="3" />
    <text x="238" y="132" fill="#92400e" fontFamily="monospace" fontSize="9" fontWeight="700" textAnchor="middle">PPE · BARRIER</text>
    <text x="160" y="170" fill="#fde68a" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">CERT IV · INFECTION CONTROL</text>
  </svg>
);

const ReceptionArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Veterinary reception desk">
    <defs>
      <linearGradient id="recBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#172554" />
        <stop offset="1" stopColor="#1d4ed8" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#recBg)" />
    {/* monitor with appointment list */}
    <rect x="34" y="30" width="150" height="100" rx="8" fill="#0b1220" stroke="#93c5fd" strokeWidth="2" />
    <rect x="44" y="40" width="130" height="80" rx="4" fill="#1e293b" />
    {Array.from({ length: 4 }).map((_, i) => (
      <g key={`appt-${i}`}>
        <rect x="52" y={50 + i * 17} width="20" height="9" rx="2" fill="#60a5fa" />
        <rect x="80" y={50 + i * 17} width="86" height="9" rx="2" fill="#334155" />
      </g>
    ))}
    <rect x="92" y="130" width="34" height="22" fill="#0b1220" stroke="#93c5fd" strokeWidth="2" />
    {/* desk bell */}
    <path d="M214 120 a26 26 0 0 1 52 0 z" fill="#cbd5e1" />
    <rect x="206" y="120" width="68" height="8" rx="3" fill="#94a3b8" />
    <circle cx="240" cy="90" r="5" fill="#cbd5e1" />
    <rect x="237" y="86" width="6" height="8" fill="#94a3b8" />
    {/* phone handset */}
    <path d="M214 44 c4 -6 14 -6 18 0 l-4 10 c-2 4 -6 4 -6 10 c0 8 8 16 16 16 c6 0 6 -4 10 -6 l10 -4 c6 4 6 14 0 18 c-22 10 -54 -22 -44 -44 z" fill="#34d399" />
    <text x="160" y="170" fill="#bfdbfe" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">CERT IV · CLIENT SERVICE</text>
  </svg>
);

/* ---- Inline SVG illustrations (robust, on-brand, no external images) ---- */

const BloodArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Blood results report">
    <defs>
      <linearGradient id="bloodBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#451a03" />
        <stop offset="1" stopColor="#b45309" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#bloodBg)" />
    {/* results report card */}
    <rect x="28" y="24" width="190" height="132" rx="8" fill="#fffbeb" stroke="#fcd34d" strokeWidth="2" />
    <rect x="40" y="36" width="120" height="8" rx="3" fill="#b45309" />
    {[58, 76, 94, 112, 130].map((y, i) => (
      <g key={y}>
        <rect x="40" y={y} width="44" height="6" rx="3" fill="#78716c" />
        {/* range track */}
        <rect x="92" y={y + 1} width="80" height="4" rx="2" fill="#e7e5e4" />
        <rect x={110} y={y + 1} width="34" height="4" rx="2" fill="#6ee7b7" />
        {/* marker — some out of range (red), some normal (teal) */}
        <circle cx={i === 1 ? 168 : i === 3 ? 96 : 126} cy={y + 3} r="4.5" fill={i === 1 || i === 3 ? "#dc2626" : "#0f766e"} stroke="#fff" strokeWidth="1.5" />
        {/* flag */}
        <text x="182" y={y + 6} fill={i === 1 ? "#dc2626" : i === 3 ? "#dc2626" : "#0f766e"} fontFamily="monospace" fontSize="9" fontWeight="700">{i === 1 ? "H" : i === 3 ? "L" : "N"}</text>
      </g>
    ))}
    {/* blood tube */}
    <g transform="translate(248 40)">
      <rect x="0" y="0" width="30" height="96" rx="14" fill="#7f1d1d" stroke="#fca5a5" strokeWidth="2" />
      <rect x="0" y="40" width="30" height="56" rx="14" fill="#dc2626" />
      <rect x="-3" y="-8" width="36" height="14" rx="4" fill="#a855f7" />
    </g>
    {/* blood drop */}
    <path d="M263 150 c-7 -10 -7 -16 0 -22 c7 6 7 12 0 22 z" fill="#fecaca" />
    <text x="123" y="172" fill="#fcd34d" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">CERT IV · BLOODWORK</text>
  </svg>
);

const CrisisArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Anaesthetic monitor alarm">
    <defs>
      <linearGradient id="crisisBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#062a30" />
        <stop offset="1" stopColor="#0e3d4a" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#crisisBg)" />
    {/* monitor bezel */}
    <rect x="24" y="22" width="272" height="120" rx="10" fill="#0b1220" stroke="#2b6f86" strokeWidth="2" />
    {/* ECG that drops away (deteriorating) */}
    <path d="M34 60 H70 l8 0 l6 -22 l8 40 l6 -18 H140 l8 0 l5 -10 l6 16 l5 -6 H210 H286" fill="none" stroke="#39FF14" strokeWidth="2.4" strokeLinejoin="round" opacity="0.9" />
    {/* falling plethysmograph */}
    <path d="M34 104 q14 -16 28 0 q8 10 16 0 q14 -10 28 0 q8 6 16 0 q14 -4 28 0 H286" fill="none" stroke="#00FFFF" strokeWidth="2" opacity="0.85" />
    {/* numerics */}
    <text x="214" y="52" fill="#39FF14" fontFamily="monospace" fontSize="22" fontWeight="700">42</text>
    <text x="214" y="64" fill="#5b6b82" fontFamily="monospace" fontSize="8">HR bpm</text>
    {/* red BP alarm box */}
    <rect x="210" y="78" width="74" height="40" rx="5" fill="#3b0a0a" stroke="#f87171" strokeWidth="2" />
    <text x="218" y="96" fill="#fca5a5" fontFamily="monospace" fontSize="9" fontWeight="700">BP LOW</text>
    <text x="218" y="112" fill="#fecaca" fontFamily="monospace" fontSize="15" fontWeight="700">62/38</text>
    {/* alarm bell */}
    <g transform="translate(44 126)">
      <path d="M0 -6 a8 8 0 0 1 16 0 c0 8 4 9 4 12 H-4 c0 -3 4 -4 4 -12 z" fill="#f87171" />
      <circle cx="8" cy="12" r="2.5" fill="#f87171" />
    </g>
    <text x="160" y="166" fill="#7fdbff" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">CERT IV · CRISIS RESPONSE</text>
  </svg>
);

const AnaesthesiaArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Anaesthesia monitor">
    <defs>
      <linearGradient id="anaBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0b1220" />
        <stop offset="1" stopColor="#111c2e" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#anaBg)" />
    {Array.from({ length: 16 }).map((_, i) => (
      <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20} y2="180" stroke="#1b2a40" strokeWidth="1" />
    ))}
    {Array.from({ length: 9 }).map((_, i) => (
      <line key={`h${i}`} x1="0" y1={i * 20} x2="320" y2={i * 20} stroke="#1b2a40" strokeWidth="1" />
    ))}
    <path
      d="M0 60 H40 l8 0 l6 -28 l6 44 l6 -16 H100 l8 0 l6 -28 l6 44 l6 -16 H180 l8 0 l6 -28 l6 44 l6 -16 H320"
      fill="none"
      stroke="#39FF14"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M0 130 q20 -34 40 0 q8 14 16 0 q20 -34 40 0 q8 14 16 0 q20 -34 40 0 q8 14 16 0 q20 -34 40 0 q8 14 16 0 q20 -34 40 0"
      fill="none"
      stroke="#00FFFF"
      strokeWidth="2.2"
      opacity="0.9"
    />
    <text x="232" y="40" fill="#39FF14" fontFamily="monospace" fontSize="26" fontWeight="700">112</text>
    <text x="232" y="54" fill="#5b6b82" fontFamily="monospace" fontSize="9">HR bpm</text>
    <text x="232" y="118" fill="#00FFFF" fontFamily="monospace" fontSize="22" fontWeight="700">98%</text>
    <text x="232" y="132" fill="#5b6b82" fontFamily="monospace" fontSize="9">SpO₂</text>
  </svg>
);

const FluidPumpArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Infusion pump">
    <defs>
      <linearGradient id="pumpBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0c2230" />
        <stop offset="1" stopColor="#0a2e3a" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#pumpBg)" />
    <line x1="70" y1="14" x2="70" y2="170" stroke="#3a5360" strokeWidth="4" />
    <line x1="46" y1="18" x2="94" y2="18" stroke="#3a5360" strokeWidth="4" strokeLinecap="round" />
    <rect x="52" y="24" width="36" height="56" rx="6" fill="#0e3a4a" stroke="#2b6f86" strokeWidth="2" />
    <rect x="52" y="44" width="36" height="36" rx="6" fill="#1fb6d6" opacity="0.85" />
    <line x1="70" y1="80" x2="70" y2="104" stroke="#2b6f86" strokeWidth="2" />
    <rect x="64" y="104" width="12" height="20" rx="3" fill="#0e3a4a" stroke="#2b6f86" strokeWidth="1.5" />
    <circle cx="70" cy="112" r="2" fill="#1fb6d6" />
    <path d="M70 124 V150 H150" fill="none" stroke="#2b6f86" strokeWidth="2.5" />
    <rect x="150" y="56" width="140" height="96" rx="10" fill="#16242c" stroke="#334a55" strokeWidth="2" />
    <rect x="162" y="68" width="116" height="46" rx="5" fill="#02110d" stroke="#114d3a" strokeWidth="1.5" />
    <text x="170" y="92" fill="#39FF14" fontFamily="monospace" fontSize="20" fontWeight="700">120</text>
    <text x="232" y="92" fill="#7fffd4" fontFamily="monospace" fontSize="10">mL/h</text>
    <text x="170" y="108" fill="#2fa37a" fontFamily="monospace" fontSize="9">VTBI 180 mL · INFUSING</text>
    {Array.from({ length: 9 }).map((_, i) => (
      <rect
        key={`keypad-dot-${i % 3}-${Math.floor(i / 3)}`}
        x={164 + (i % 3) * 38}
        y={122 + Math.floor(i / 3) * 10}
        width="30"
        height="6"
        rx="3"
        fill="#2a3a43"
      />
    ))}
  </svg>
);

const IPPVArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Anaesthetic machine">
    <defs>
      <linearGradient id="ippvBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0a2233" />
        <stop offset="1" stopColor="#0c3142" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#ippvBg)" />
    <rect x="22" y="86" width="22" height="64" rx="10" fill="#22c55e" />
    <rect x="29" y="78" width="8" height="12" rx="3" fill="#16a34a" />
    <rect x="64" y="92" width="14" height="52" rx="6" fill="#0e3a4a" stroke="#2b6f86" strokeWidth="1.5" />
    <circle cx="71" cy="116" r="4" fill="#38bdf8" />
    <rect x="96" y="104" width="40" height="40" rx="6" fill="#16242c" stroke="#334a55" strokeWidth="1.5" />
    <circle cx="116" cy="124" r="11" fill="none" stroke="#7fffd4" strokeWidth="2.5" />
    <path d="M44 118 H64 M78 116 H96 M136 124 C170 70 210 60 246 64" fill="none" stroke="#2b6f86" strokeWidth="3" />
    <rect x="150" y="40" width="26" height="48" rx="5" fill="#0e3a4a" stroke="#2b6f86" strokeWidth="1.5" />
    <rect x="153" y="44" width="20" height="40" rx="3" fill="#34d399" opacity="0.7" />
    <circle cx="208" cy="54" r="12" fill="#0e3a4a" stroke="#39FF14" strokeWidth="2.5" />
    <circle cx="252" cy="56" r="18" fill="#02110d" stroke="#114d3a" strokeWidth="2" />
    <line x1="252" y1="56" x2="262" y2="44" stroke="#39FF14" strokeWidth="2.5" strokeLinecap="round" />
    <ellipse cx="162" cy="128" rx="20" ry="26" fill="#1fb6d6" opacity="0.85" />
    <path d="M276 96 C276 120 264 128 260 140 C257 148 266 152 270 146 C276 138 278 122 278 110 Z" fill="#fca5a5" stroke="#fb7185" strokeWidth="1.5" />
    <path d="M284 96 C284 120 296 128 300 140 C303 148 294 152 290 146 C284 138 282 122 282 110 Z" fill="#fca5a5" stroke="#fb7185" strokeWidth="1.5" />
    <text x="160" y="170" fill="#7fdbff" fontFamily="monospace" fontSize="11" fontWeight="700" textAnchor="middle">Machine setup · leak test · IPPV</text>
  </svg>
);

const DrugCalcArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Drug volume calculator">
    <defs>
      <linearGradient id="calcBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#10212e" />
        <stop offset="1" stopColor="#0c2a2f" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#calcBg)" />
    <rect x="40" y="40" width="40" height="90" rx="6" fill="#0e3a4a" stroke="#2b6f86" strokeWidth="2" />
    <rect x="40" y="86" width="40" height="44" rx="3" fill="#34d399" opacity="0.55" />
    <rect x="46" y="30" width="28" height="14" rx="3" fill="#475569" />
    {[58, 72, 86, 100, 114].map((y) => (
      <line key={y} x1="68" y1={y} x2="78" y2={y} stroke="#7fdbff" strokeWidth="1.4" />
    ))}
    <rect x="108" y="78" width="120" height="22" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5" />
    <rect x="108" y="82" width="78" height="14" rx="2" fill="#38bdf8" opacity="0.8" />
    <rect x="186" y="80" width="6" height="18" fill="#64748b" />
    <rect x="192" y="82" width="40" height="14" rx="2" fill="#cbd5e1" />
    <rect x="232" y="76" width="6" height="26" rx="2" fill="#94a3b8" />
    <line x1="108" y1="89" x2="84" y2="89" stroke="#94a3b8" strokeWidth="2.5" />
    {[120, 134, 148, 162, 176].map((x) => (
      <line key={x} x1={x} y1="78" x2={x} y2="72" stroke="#64748b" strokeWidth="1.2" />
    ))}
    <rect x="150" y="118" width="138" height="46" rx="6" fill="#02110d" stroke="#114d3a" strokeWidth="1.5" />
    <text x="162" y="140" fill="#5b6b82" fontFamily="monospace" fontSize="9">VOLUME (mL)</text>
    <text x="162" y="158" fill="#39FF14" fontFamily="monospace" fontSize="20" fontWeight="700">0.36</text>
    <text x="232" y="150" fill="#7fffd4" fontFamily="monospace" fontSize="11">= mg·kg ÷ mg/mL</text>
  </svg>
);

const TriageArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Ring-in triage console">
    <defs>
      <linearGradient id="triBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0a1626" />
        <stop offset="1" stopColor="#0c2233" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#triBg)" />
    <rect x="24" y="28" width="150" height="124" rx="10" fill="#0e2336" stroke="#1d3350" strokeWidth="1.5" />
    <circle cx="40" cy="44" r="3.5" fill="#34d399" />
    <text x="50" y="47" fill="#34d399" fontFamily="monospace" fontSize="9">LIVE · LINE 1</text>
    <text x="146" y="47" fill="#fb7185" fontFamily="monospace" fontSize="8">REC</text>
    <circle cx="48" cy="74" r="13" fill="#13314a" />
    <path d="M42 70 a3 3 0 0 1 3 -3 l3 0 1 4 -2 2 a10 10 0 0 0 5 5 l2 -2 4 1 0 3 a3 3 0 0 1 -3 3 c-8 0 -16 -8 -16 -16 Z" fill="#6ee7b7" />
    <text x="68" y="70" fill="#64748b" fontFamily="monospace" fontSize="8">CALLER</text>
    <text x="68" y="82" fill="#e2e8f0" fontFamily="monospace" fontSize="11" fontWeight="700">Ms Carter</text>
    {[100, 114, 128].map((y, i) => (
      <g key={y}>
        <rect x="36" y={y} width="40" height="5" rx="2.5" fill="#1d3350" />
        <rect x="82" y={y} width={[70, 54, 78][i]} height="5" rx="2.5" fill="#27496b" />
      </g>
    ))}
    <rect x="190" y="36" width="104" height="30" rx="8" fill="#e2e8f0" />
    <rect x="200" y="44" width="74" height="5" rx="2.5" fill="#94a3b8" />
    <rect x="200" y="54" width="58" height="5" rx="2.5" fill="#94a3b8" />
    <rect x="208" y="78" width="104" height="26" rx="8" fill="#0047AB" />
    <rect x="218" y="86" width="80" height="5" rx="2.5" fill="#7da7e8" />
    <rect x="190" y="118" width="58" height="16" rx="8" fill="#0e2336" stroke="#34d399" strokeWidth="1.4" />
    <text x="198" y="129" fill="#6ee7b7" fontFamily="monospace" fontSize="8">red flag</text>
    <rect x="254" y="118" width="40" height="16" rx="8" fill="#0e2336" stroke="#fb7185" strokeWidth="1.4" />
    <circle cx="263" cy="126" r="2.5" fill="#fb7185" />
    <text x="160" y="170" fill="#7fdbff" fontFamily="monospace" fontSize="10" fontWeight="700" textAnchor="middle">CERT IV · TRIAGE RUBRIC</text>
  </svg>
);

const DentalArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Dental charting">
    <defs>
      <linearGradient id="dentBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#062925" />
        <stop offset="1" stopColor="#0a3b34" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#dentBg)" />
    <path d="M40 132 Q160 64 280 132" fill="none" stroke="#0f5249" strokeWidth="2" />
    {Array.from({ length: 11 }).map((_, i) => {
      const t = i / 10;
      const x = 40 + t * 240;
      const y = 132 - Math.sin(Math.PI * t) * 64;
      const flagged = i === 2 || i === 5 || i === 8;
      return (
        <g key={`utooth-${i}`} transform={`translate(${x} ${y})`}>
          <rect x="-7" y="-2" width="14" height="22" rx="4" fill={flagged ? "#0a3b34" : "#e7faf6"} stroke={flagged ? "#f43f5e" : "#5eead4"} strokeWidth="1.6" />
          {flagged && <text x="0" y="13" textAnchor="middle" fontFamily="monospace" fontSize="9" fontWeight="700" fill="#fb7185">X</text>}
        </g>
      );
    })}
    <line x1="160" y1="58" x2="160" y2="150" stroke="#0f5249" strokeWidth="1.5" strokeDasharray="3 3" />
    <g fontFamily="monospace" fontSize="9" fontWeight="700">
      <rect x="40" y="150" width="20" height="14" rx="3" fill="#dc2626" />
      <text x="50" y="160" textAnchor="middle" fill="#fff">X</text>
      <rect x="66" y="150" width="20" height="14" rx="3" fill="#ea580c" />
      <text x="76" y="160" textAnchor="middle" fill="#fff">F</text>
      <rect x="92" y="150" width="24" height="14" rx="3" fill="#c026d3" />
      <text x="104" y="160" textAnchor="middle" fill="#fff">RL</text>
      <rect x="122" y="150" width="24" height="14" rx="3" fill="#ca8a04" />
      <text x="134" y="160" textAnchor="middle" fill="#fff">CA</text>
      <rect x="152" y="150" width="24" height="14" rx="3" fill="#b45309" />
      <text x="164" y="160" textAnchor="middle" fill="#fff">PD</text>
    </g>
    <text x="280" y="40" textAnchor="end" fill="#5eead4" fontFamily="monospace" fontSize="11" fontWeight="700">109 · P4</text>
    <text x="280" y="54" textAnchor="end" fill="#2f9e8d" fontFamily="monospace" fontSize="9">Modified Triadan</text>
  </svg>
);

const NutritionArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Nutrition calculator">
    <defs>
      <linearGradient id="nutBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#10261c" />
        <stop offset="1" stopColor="#0c2e22" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#nutBg)" />
    <path d="M40 112 a52 22 0 0 0 104 0 q0 30 -52 30 q-52 0 -52 -30 Z" fill="#154633" stroke="#2f9e7a" strokeWidth="2" />
    <ellipse cx="92" cy="112" rx="52" ry="20" fill="#1c5a42" stroke="#34d399" strokeWidth="1.5" />
    {[[72, 108], [92, 114], [112, 108], [82, 118], [104, 118], [92, 104]].map(([x, y], i) => (
      <circle key={`kib-${i}`} cx={x} cy={y} r="5.5" fill="#caa15a" stroke="#a87f3c" strokeWidth="1" />
    ))}
    <rect x="170" y="40" width="118" height="58" rx="6" fill="#02110d" stroke="#114d3a" strokeWidth="1.5" />
    <text x="180" y="58" fill="#5b6b82" fontFamily="monospace" fontSize="9">RER = 70 × kg^0.75</text>
    <text x="180" y="80" fill="#39FF14" fontFamily="monospace" fontSize="20" fontWeight="700">216</text>
    <text x="236" y="80" fill="#7fffd4" fontFamily="monospace" fontSize="11">kcal/day</text>
    <text x="170" y="120" fill="#5b6b82" fontFamily="monospace" fontSize="9">BCS</text>
    {Array.from({ length: 9 }).map((_, i) => (
      <rect key={`bcs-${i}`} x={196 + i * 10} y={110} width="7" height="12" rx="2" fill={i === 7 ? "#f59e0b" : "#1f4a39"} />
    ))}
    <text x="160" y="166" fill="#7fffd4" fontFamily="monospace" fontSize="10" fontWeight="700" textAnchor="middle">CERT IV · NUTRITION DRILLS</text>
  </svg>
);

const UrinalysisArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Urinalysis bench">
    <defs>
      <linearGradient id="uaBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#231a05" />
        <stop offset="1" stopColor="#2e2207" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#uaBg)" />
    {/* specimen tube */}
    <path d="M40 36 H66 V126 a13 13 0 0 1 -26 0 Z" fill="#1c1606" stroke="#5a4a1c" strokeWidth="2" />
    <rect x="36" y="30" width="34" height="9" rx="3" fill="#6b5a26" />
    <path d="M41 70 H65 V126 a12 12 0 0 1 -24 0 Z" fill="#eccf38" />
    <ellipse cx="53" cy="70" rx="12" ry="3" fill="#fff" opacity="0.35" />
    {/* refractometer scope */}
    <circle cx="150" cy="78" r="40" fill="#0b1622" stroke="#3a5468" strokeWidth="2" />
    <clipPath id="uaScope"><circle cx="150" cy="78" r="36" /></clipPath>
    <g clipPath="url(#uaScope)">
      <rect x="114" y="42" width="72" height="44" fill="#cfeaff" />
      <rect x="114" y="86" width="72" height="40" fill="#13415e" />
      <line x1="114" y1="86" x2="186" y2="86" stroke="#0a2330" strokeWidth="2" />
      {[58, 72, 86, 100].map((y) => (
        <line key={y} x1="140" y1={y} x2="166" y2={y} stroke={y >= 86 ? "#bcd6e6" : "#234a63"} strokeWidth="1" />
      ))}
    </g>
    <text x="150" y="134" textAnchor="middle" fill="#9ec6e0" fontFamily="monospace" fontSize="9" fontWeight="700">USG 1.030</text>
    {/* dipstick with coloured pads */}
    <rect x="214" y="34" width="16" height="116" rx="3" fill="#f3f0e6" stroke="#b9b29a" strokeWidth="1.5" />
    {[
      { y: 40, c: "#e3b24a" },
      { y: 58, c: "#a9cf72" },
      { y: 76, c: "#8a5a2a" },
      { y: 94, c: "#c25b77" },
      { y: 112, c: "#2f7d6a" },
      { y: 130, c: "#d8a06e" },
    ].map((p) => (
      <rect key={p.y} x="216" y={p.y} width="12" height="14" rx="2" fill={p.c} />
    ))}
    {/* reference chart */}
    {[0, 1, 2, 3].map((row) => (
      <g key={row}>
        {["#e9e25a", "#a9cf72", "#79b97e", "#4f9e86"].map((c, i) => (
          <rect key={c} x={250 + i * 14} y={42 + row * 18} width="11" height="12" rx="2" fill={c} opacity="0.92" />
        ))}
      </g>
    ))}
    <text x="160" y="170" fill="#e0b873" fontFamily="monospace" fontSize="10" fontWeight="700" textAnchor="middle">CERT IV · URINALYSIS</text>
  </svg>
);

const ClinPathArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Clinical pathology lab">
    <defs>
      <linearGradient id="cpBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#1c1303" />
        <stop offset="1" stopColor="#2a1c06" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#cpBg)" />
    {/* rack of sample tubes with coloured caps (EDTA / serum / fluoride / heparin) */}
    {[
      { x: 34, cap: "#c084fc", fill: "#7b3fb0" },
      { x: 60, cap: "#f87171", fill: "#b45309" },
      { x: 86, cap: "#cbd5e1", fill: "#94a3b8" },
      { x: 112, cap: "#4ade80", fill: "#15803d" },
    ].map((t, i) => (
      <g key={t.x}>
        <rect x={t.x} y="44" width="16" height="86" rx="8" fill="#0e1726" stroke="#3a4658" strokeWidth="1.5" />
        <rect x={t.x} y="92" width="16" height="38" rx="8" fill={t.fill} opacity="0.85" />
        <rect x={t.x - 1} y="38" width="18" height="12" rx="4" fill={t.cap} />
      </g>
    ))}
    {/* microscope silhouette */}
    <g stroke="#d8a657" strokeWidth="3" fill="none" strokeLinecap="round">
      <line x1="150" y1="138" x2="184" y2="138" />
      <path d="M167 138 V112" />
      <path d="M167 112 q-2 -22 18 -30" />
      <circle cx="190" cy="78" r="9" fill="#2a1c06" />
      <path d="M167 120 h14" />
    </g>
    {/* results readout with H/L flags */}
    <rect x="208" y="40" width="92" height="100" rx="8" fill="#02110d" stroke="#5b4a16" strokeWidth="1.5" />
    {[
      { y: 58, label: "PCV", flag: "L", c: "#60a5fa" },
      { y: 76, label: "WBC", flag: "H", c: "#f87171" },
      { y: 94, label: "Urea", flag: "H", c: "#f87171" },
      { y: 112, label: "K⁺", flag: "H", c: "#f87171" },
    ].map((r) => (
      <g key={r.label} fontFamily="monospace" fontSize="9">
        <text x="216" y={r.y} fill="#caa15a">{r.label}</text>
        <rect x="262" y={r.y - 9} width="14" height="11" rx="2" fill={r.flag === "H" ? "#7f1d1d" : "#1e3a5f"} />
        <text x="269" y={r.y - 1} fill={r.c} textAnchor="middle" fontWeight="700">{r.flag}</text>
      </g>
    ))}
    <text x="160" y="166" fill="#e0b873" fontFamily="monospace" fontSize="10" fontWeight="700" textAnchor="middle">CERT IV · CLINICAL PATHOLOGY</text>
  </svg>
);

const AdmissionArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Admissions desk">
    <defs>
      <linearGradient id="admBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0a1626" />
        <stop offset="1" stopColor="#10243d" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#admBg)" />
    <rect x="30" y="30" width="120" height="120" rx="8" fill="#e8eef6" stroke="#9fb4d0" strokeWidth="2" />
    <rect x="70" y="24" width="40" height="14" rx="4" fill="#7d93b3" />
    <rect x="44" y="50" width="92" height="6" rx="3" fill="#0047AB" opacity="0.85" />
    {[66, 80, 94].map((y) => (
      <g key={y}>
        <rect x="44" y={y} width="10" height="10" rx="2" fill="none" stroke="#34d399" strokeWidth="2" />
        <path d={`M45 ${y + 5} l3 3 l6 -7`} fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="60" y={y + 2} width="68" height="5" rx="2.5" fill="#b9c7dc" />
      </g>
    ))}
    <rect x="44" y="112" width="50" height="14" rx="7" fill="#0047AB" />
    <text x="69" y="122" textAnchor="middle" fill="#fff" fontFamily="monospace" fontSize="8" fontWeight="700">CONSENT</text>
    <rect x="100" y="112" width="36" height="14" rx="7" fill="#0e2336" stroke="#fb7185" strokeWidth="1.4" />
    <text x="118" y="122" textAnchor="middle" fill="#fb7185" fontFamily="monospace" fontSize="8" fontWeight="700">NPO?</text>
    <rect x="176" y="44" width="116" height="30" rx="15" fill="none" stroke="#5ec0ff" strokeWidth="2.5" />
    <text x="186" y="63" fill="#5ec0ff" fontFamily="monospace" fontSize="10" fontWeight="700">ID · BELLA</text>
    <rect x="176" y="92" width="116" height="58" rx="8" fill="#02110d" stroke="#114d3a" strokeWidth="1.5" />
    <text x="188" y="112" fill="#5b6b82" fontFamily="monospace" fontSize="9">WEIGH-IN</text>
    <text x="188" y="134" fill="#39FF14" fontFamily="monospace" fontSize="20" fontWeight="700">26.4</text>
    <text x="252" y="134" fill="#7fffd4" fontFamily="monospace" fontSize="12">kg</text>
    <text x="160" y="170" fill="#7fdbff" fontFamily="monospace" fontSize="10" fontWeight="700" textAnchor="middle">CERT IV · PATIENT ADMISSION</text>
  </svg>
);

const WardRoundsArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Ward rounds medication chart">
    <defs>
      <linearGradient id="wrBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0c2230" />
        <stop offset="1" stopColor="#0e2e2a" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#wrBg)" />
    {/* clipboard treatment sheet */}
    <rect x="28" y="26" width="150" height="128" rx="8" fill="#f3f6fb" stroke="#9fb4d0" strokeWidth="2" />
    <rect x="78" y="20" width="50" height="14" rx="4" fill="#64748b" />
    <rect x="40" y="44" width="126" height="7" rx="3" fill="#0047AB" opacity="0.85" />
    {[60, 76, 92, 108, 124].map((y, i) => (
      <g key={y}>
        <rect x="40" y={y} width="84" height="5" rx="2.5" fill="#c2cee0" />
        {/* status tick / due dot */}
        {i === 2 ? (
          <circle cx="156" cy={y + 2} r="4.5" fill="#f59e0b" />
        ) : i < 2 ? (
          <path d={`M150 ${y + 2} l3 3 l6 -7`} fill="none" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <rect x="150" y={y - 1} width="12" height="7" rx="2" fill="#cbd5e1" />
        )}
      </g>
    ))}
    {/* syringe */}
    <g transform="rotate(-24 232 70)">
      <rect x="200" y="62" width="78" height="16" rx="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5" />
      <rect x="204" y="65" width="48" height="10" rx="2" fill="#38bdf8" opacity="0.85" />
      <rect x="252" y="63" width="6" height="14" fill="#64748b" />
      <rect x="258" y="65" width="22" height="10" rx="2" fill="#cbd5e1" />
      <rect x="278" y="60" width="6" height="20" rx="2" fill="#94a3b8" />
      <line x1="200" y1="70" x2="178" y2="70" stroke="#94a3b8" strokeWidth="2.5" />
    </g>
    {/* vial + readout */}
    <rect x="206" y="96" width="22" height="48" rx="4" fill="#0e3a4a" stroke="#2b6f86" strokeWidth="1.5" />
    <rect x="206" y="116" width="22" height="28" rx="4" fill="#34d399" opacity="0.55" />
    <rect x="209" y="90" width="16" height="8" rx="2" fill="#475569" />
    <rect x="238" y="98" width="62" height="46" rx="6" fill="#02110d" stroke="#114d3a" strokeWidth="1.5" />
    <text x="246" y="116" fill="#5b6b82" fontFamily="monospace" fontSize="8">DOSE → VOL</text>
    <text x="246" y="134" fill="#39FF14" fontFamily="monospace" fontSize="16" fontWeight="700">0.96</text>
    <text x="284" y="134" fill="#7fffd4" fontFamily="monospace" fontSize="8">mL</text>
    <text x="160" y="170" fill="#7fdbff" fontFamily="monospace" fontSize="10" fontWeight="700" textAnchor="middle">CERT IV · MEDICATION ROUNDS</text>
  </svg>
);

const DrugSafeArt = () => (
  <svg viewBox="0 0 320 180" className="w-full h-full" role="img" aria-label="Controlled drug safe and S8 register">
    <defs>
      <linearGradient id="dsBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#2a1604" />
        <stop offset="1" stopColor="#3a2208" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#dsBg)" />
    {/* controlled-drug safe */}
    <rect x="26" y="30" width="150" height="124" rx="8" fill="#1c2733" stroke="#b45309" strokeWidth="3" />
    <rect x="38" y="42" width="126" height="100" rx="5" fill="#0f1822" stroke="#334155" strokeWidth="1.5" />
    {/* S8 label */}
    <rect x="58" y="54" width="86" height="26" rx="4" fill="#b45309" />
    <text x="101" y="73" fill="#fff" fontFamily="monospace" fontSize="17" fontWeight="800" textAnchor="middle">S8</text>
    {/* combination dial / lock */}
    <circle cx="101" cy="112" r="20" fill="#1e293b" stroke="#f59e0b" strokeWidth="2.5" />
    <circle cx="101" cy="112" r="11" fill="#0b1220" stroke="#94a3b8" strokeWidth="1.5" />
    <line x1="101" y1="112" x2="101" y2="98" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
    {/* handle */}
    <rect x="150" y="96" width="8" height="34" rx="4" fill="#94a3b8" />
    {/* vial inside frame, right side */}
    <rect x="200" y="60" width="26" height="60" rx="5" fill="#0e3a4a" stroke="#2b6f86" strokeWidth="1.5" />
    <rect x="200" y="86" width="26" height="34" rx="5" fill="#fbbf24" opacity="0.55" />
    <rect x="204" y="52" width="18" height="9" rx="2" fill="#475569" />
    {/* S8 register ledger */}
    <rect x="236" y="70" width="62" height="78" rx="6" fill="#f3f6fb" stroke="#9fb4d0" strokeWidth="2" />
    <rect x="244" y="80" width="46" height="6" rx="3" fill="#b45309" opacity="0.85" />
    {[94, 106, 118, 130].map((y) => (
      <g key={y}>
        <rect x="244" y={y} width="30" height="4" rx="2" fill="#c2cee0" />
        <rect x="278" y={y - 1} width="12" height="5" rx="2" fill="#34d399" opacity="0.7" />
      </g>
    ))}
    <text x="160" y="170" fill="#fbbf24" fontFamily="monospace" fontSize="10" fontWeight="700" textAnchor="middle">CERT IV · SCHEDULED DRUGS</text>
  </svg>
);

export const ToothIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 3c-2 0-3.5 1.6-3.5 4 0 2 .6 3.2 1 5 .5 2.2.4 6 1.6 8 .5.8 1.6.7 2-.2.6-1.4.7-3.4 1.4-3.4s.8 2 1.4 3.4c.4.9 1.5 1 2 .2 1.2-2 1.1-5.8 1.6-8 .4-1.8 1-3 1-5 0-2.4-1.5-4-3.5-4-1.6 0-2.4 1-4 1S8.6 3 7 3Z" />
  </svg>
);

/* The route a simulator launches to (intake is shared by the anaesthesia flow). */
export const SIM_ROUTES = {
  anaesthesia: "/intake",
  anaesthesia2: "/intake2",
  fluidpump: "/fluid-pump",
  anaesmachine: "/anaes-machine",
  ippv: "/ippv",
  drugcalc: "/drug-calc",
  triage: "/triage",
  admission: "/admission",
  clinpath: "/clinpath",
  urinalysis: "/urinalysis",
  dental: "/dental",
  drugstorage: "/drug-storage",
  nutrition: "/nutrition",
  wardrounds: "/ward-rounds",
  bloodwork: "/bloodwork",
  conflict: "/sim/conflict",
  dispensing: "/sim/dispensing",
  radiography: "/sim/radiography",
  asepsis: "/sim/asepsis",
  grief: "/sim/grief",
  emergency: "/sim/emergency",
  infection: "/sim/infection",
  reception: "/sim/reception",
  anaescrisis: "/sim/anaescrisis",
};

export const SIMULATORS = [
  {
    id: "anaesthesia",
    title: "Veterinary Anaesthetic Simulator",
    blurb:
      "Plan a balanced anaesthetic protocol, then run a live multi-parameter monitor with animated ECG, SpO₂ and capnograph waveforms. Manage simulated emergencies and complete an AAHA monitoring log.",
    tags: ["Monitoring", "Emergencies", "AAHA log"],
    Art: AnaesthesiaArt,
    Icon: Activity,
  },
  {
    id: "anaesthesia2",
    title: "Veterinary Anaesthesia Simulator 2",
    blurb:
      "Plan a balanced anaesthetic protocol, then run a live multi-parameter monitor with animated ECG, SpO₂ and capnograph waveforms. Manage simulated emergencies and complete an AAHA monitoring log.",
    tags: ["Monitoring", "Emergencies", "AAHA log"],
    Art: AnaesthesiaArt,
    Icon: Activity,
  },
  {
    id: "fluidpump",
    title: "Fluid Delivery Pump",
    blurb:
      "Read the clinical fluid order, calculate the correct rate for surgery or maintenance, and program a working volumetric infusion pump. Watch it deliver in real time.",
    tags: ["Fluid therapy", "Infusion pump", "Dose maths"],
    Art: FluidPumpArt,
    Icon: Stethoscope,
  },
  {
    id: "anaesmachine",
    title: "Anaesthetic Machine Setup",
    blurb:
      "Prepare an anaesthetic machine end-to-end on a live schematic — initial setup (O₂, vaporiser, soda lime, circuit & bag selection, scavenging, pre-use sign-off) followed by a low-pressure leak test that must hold 30 cmH₂O. Phase-scored against Cert IV machine-preparation competencies.",
    tags: ["Machine setup", "Leak test", "Pre-use checks"],
    Art: IPPVArt,
    Icon: Wrench,
  },
  {
    id: "ippv",
    title: "IPPV & Manual Ventilation",
    blurb:
      "The machine is set up and leak-tested — now deliver safe manual breaths across three back-to-back clinical rounds (cat, dog, apnoeic post-induction). Manage the pop-off valve, respect each patient's PIP ceiling, and restore maintenance anaesthesia between rounds.",
    tags: ["IPPV", "Manual ventilation", "Airway pressure"],
    Art: IPPVArt,
    Icon: Wind,
  },
  {
    id: "drugcalc",
    title: "Drug Volume Calculator",
    blurb:
      "Choose your anaesthetic drugs, then calculate the exact volume to draw up from the ordered dose, the patient's weight and the drug concentration. Type your answers and get instantly graded.",
    tags: ["Dose maths", "Volume calc", "Self-test"],
    Art: DrugCalcArt,
    Icon: Calculator,
  },
  {
    id: "triage",
    title: "Ring-In Triage Console",
    blurb:
      "Take an incoming call, catch the red flags as the owner speaks, then decide history, advice and urgency. Graded live against a Cert IV triage rubric with assessor coaching and a full debrief.",
    tags: ["Phone triage", "Red flags", "Cert IV rubric"],
    Art: TriageArt,
    Icon: Headset,
  },
  {
    id: "admission",
    title: "Admissions Desk",
    blurb:
      "Admit a surgical patient through five staged beats — confirm identity & procedure, secure consent & finances, run the pre-op safety check and weigh-in, prepare & house, then hand over. Catch the fasting, consent, biosecurity and patient-ID red flags. Graded against a Cert IV admission rubric.",
    tags: ["Patient ID", "Consent", "Pre-op safety", "Cert IV rubric"],
    Art: AdmissionArt,
    Icon: ClipboardList,
  },
  {
    id: "clinpath",
    title: "Clinical Pathology Lab",
    blurb:
      "Run a patient's samples from bench to report. Choose the right tubes & handling, catch pre-analytical artefacts (haemolysis, clots, delayed separation), then interpret the haematology and biochemistry panels and decide what to escalate. Graded against a Cert IV clinical-pathology rubric.",
    tags: ["Sample handling", "Haematology", "Biochemistry", "Cert IV rubric"],
    Art: ClinPathArt,
    Icon: Microscope,
  },
  {
    id: "urinalysis",
    title: "Urinalysis Bench",
    blurb:
      "Work a urine sample end to end — judge colour & clarity, read the refractometer for specific gravity, match the reagent dipstick to the reference chart, then interpret abnormal findings and choose the likely condition with a full explanation. Graded against a Cert IV urinalysis rubric.",
    tags: ["Gross appearance", "Refractometer USG", "Dipstick reading", "Cert IV rubric"],
    Art: UrinalysisArt,
    Icon: Beaker,
  },
  {
    id: "dental",
    title: "Dental Charting Trainer",
    blurb:
      "Read the patient briefing, then mark up a Modified-Triadan dental chart — identify the correct tooth and apply the right AVDC symbol for extraction, fracture, resorption, periodontitis and more. Letter-graded at the end.",
    tags: ["Modified Triadan", "AVDC symbols", "COHAT"],
    Art: DentalArt,
    Icon: ToothIcon,
  },
  {
    id: "nutrition",
    title: "Nutrition Calculator Trainer",
    blurb:
      "Fast Cert IV nutrition drills. Assess body condition, calculate RER and MER, pick the right therapeutic or life-stage diet, convert kcal into grams or cans, and educate the owner — graded within ±10% with a full debrief.",
    tags: ["RER / MER", "Diet selection", "kcal → portion"],
    Art: NutritionArt,
    Icon: Utensils,
  },
  {
    id: "wardrounds",
    title: "Ward Rounds & Medications",
    blurb:
      "Run a hospitalised patient's medication round end to end — read the treatment sheet, run the medication 'rights' checks, calculate the correct dose volume, give it by the safe route, then record and sign off. Catch the duplicate orders, unsigned doses and dose-query red flags. Graded against a Cert IV medication rubric.",
    tags: ["Treatment sheet", "5 rights", "Dose maths", "Cert IV rubric"],
    Art: WardRoundsArt,
    Icon: Pill,
  },
  {
    id: "bloodwork",
    title: "Bloodwork Interpretation",
    blurb:
      "Read a graphical haematology & biochemistry report from a real-style analyser. Flag each result as Low, Normal or High against its reference interval, then interpret what the pattern means (kidney, liver, haemolysis, inflammation) and what to do next — with teaching feedback on every result you get wrong.",
    tags: ["Reference ranges", "High / Low / Normal", "Pattern interpretation", "Teaching feedback"],
    Art: BloodArt,
    Icon: Droplet,
  },
  {
    id: "conflict",
    title: "Conflict Resolution & Difficult Conversations",
    blurb:
      "Handle the conversations that rattle a veterinary team — an angry client, grief showing up as aggression, a review threat, a tense colleague. Read the real driver, open with empathy, de-escalate, resolve while holding professional and safety boundaries, then document it. Graded against a Cert IV communication rubric.",
    tags: ["De-escalation", "Empathy", "Boundaries", "Cert IV rubric"],
    Art: ConflictArt,
    Icon: MessageSquareWarning,
  },
  {
    id: "grief",
    title: "Grief, Euthanasia & Bereavement Support",
    blurb:
      "Support a family through end-of-life care with skill and compassion — prepare the space, confirm consent and process, assist gently during the procedure, talk through aftercare and body-care options, and look after the owner and yourself.",
    tags: ["Euthanasia", "Bereavement", "Compassion"],
    Art: GriefArt,
    Icon: HeartHandshake,
  },
  {
    id: "reception",
    title: "Reception & Client Service",
    blurb:
      "Run a busy front desk well — prioritise competing demands, handle calls and bookings, manage consent and payment conversations, protect client privacy and records, and keep recalls and follow-ups on track.",
    tags: ["Triage the desk", "Privacy", "Recalls"],
    Art: ReceptionArt,
    Icon: ConciergeBell,
  },
  {
    id: "dispensing",
    title: "Dispensing & Medication Supply",
    blurb:
      "Take a prescription from the vet's order to the client's hands — read the script, run the dispensing rights, calculate the quantity, build a compliant label and counsel the owner. Catch scheduling, expiry and dosing traps. Graded against a Cert IV dispensing rubric.",
    tags: ["Dispensing rights", "Label", "Dose maths", "Cert IV rubric"],
    Art: DispensingArt,
    Icon: Pill,
  },
  {
    id: "infection",
    title: "Infection Control & Biosecurity",
    blurb:
      "Protect patients, staff and the hospital from cross-infection — risk-assess an infectious case, choose PPE, set up isolation and barrier nursing, apply the right cleaning/disinfection level, then manage clinical waste and records.",
    tags: ["Isolation", "PPE", "Disinfection", "Cert IV rubric"],
    Art: InfectionArt,
    Icon: ShieldCheck,
  },
  {
    id: "drugstorage",
    title: "Scheduled Drug Storage & Compliance",
    blurb:
      "Handle scheduled medicines the legal way — identify each drug's schedule under the Poisons Standard (S4 vs S8), store it in the correct secured location, and keep an accurate S8 controlled-drug register with running balance and discrepancy checks. Graded against Cert IV medicines-handling competencies.",
    tags: ["S4 vs S8", "Secure storage", "Drug register", "Cert IV rubric"],
    Art: DrugSafeArt,
    Icon: Lock,
  },
  {
    id: "radiography",
    title: "Radiography Positioning & Safety",
    blurb:
      "Produce a diagnostic radiograph the right way — choose the views, position and centre the patient, set exposure and collimation, apply ALARA radiation safety, then quality-check the image for motion, exposure and positioning errors.",
    tags: ["Positioning", "Exposure", "ALARA", "Image QC"],
    Art: RadiographyArt,
    Icon: ScanLine,
  },
  {
    id: "asepsis",
    title: "Surgical Asepsis & Instrument Prep",
    blurb:
      "Set up theatre and protect the sterile field — work the scrub, gown and closed-glove sequence, catch contamination breaches, identify and assemble instruments, run the autoclave correctly, and verify sterility before the patient arrives.",
    tags: ["Sterile field", "Instruments", "Sterilisation"],
    Art: AsepsisArt,
    Icon: Scissors,
  },
  {
    id: "emergency",
    title: "Emergency First Aid & CPR",
    blurb:
      "Respond when a hospitalised patient crashes — recognise the emergency and raise the alarm, run the primary survey, deliver effective CPR to RECOVER-aligned standards, support the patient, then reassess and record. Graded against a Cert IV emergency rubric.",
    tags: ["Recognition", "CPR", "RECOVER", "Cert IV rubric"],
    Art: EmergencyArt,
    Icon: HeartPulse,
  },
  {
    id: "anaescrisis",
    title: "Anaesthetic Complications & Crisis Response",
    blurb:
      "Monitoring anaesthesia when things change. Across 16 intra-operative scenarios — haemorrhage, hypoventilation, bradycardia, hypoxaemia, arrest and more — work out the expected physiological impact, how you'd detect it on your monitoring, and the correct response. Graded against a Cert IV anaesthetic-monitoring rubric.",
    tags: ["16 scenarios", "Monitoring", "Crisis response", "Cert IV rubric"],
    Art: CrisisArt,
    Icon: Activity,
  },
];

export const SIM_BY_ID = Object.fromEntries(SIMULATORS.map((s) => [s.id, s]));

/* ---- New-sim tracking -------------------------------------------------
 * Map of sim id → release date (ISO YYYY-MM-DD). When present AND within
 * NEW_WINDOW_DAYS of today, `isSimNew(id)` returns true so the UI shows
 * a small "New" ribbon on the tile. Remove the entry (or push the date
 * back past the window) when a sim is no longer newly-released.
 *
 * To announce a newly-released simulator, add its id here with today's
 * ISO date. That's it — the badge appears everywhere the sim tile renders.
 */
export const NEW_WINDOW_DAYS = 30;
export const SIM_RELEASED_AT = {
  // Seeded with a couple of recent releases so the badge is visible today.
  // Adjust / add / remove freely.
  radiography: "2026-07-01",
  asepsis: "2026-07-01",
  anaescrisis: "2026-06-28",
  anaesmachine: "2026-07-14",
  ippv: "2026-07-14",
};

export function isSimNew(id) {
  const iso = SIM_RELEASED_AT[id];
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  const ageMs = Date.now() - t;
  return ageMs >= 0 && ageMs <= NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

/* ---- Clinical sections (home page) ---- */
export const SECTIONS = [
  {
    id: "triage-admission",
    n: 1,
    title: "Triage and Admission",
    blurb:
      "Take incoming calls, recognise red flags and assign the right urgency. Front-line phone-triage and admission skills.",
    accent: "#0047AB",
    Icon: Headset,
    sims: ["triage", "admission", "emergency"],
  },
  {
    id: "surgery-anaesthesia",
    n: 2,
    title: "Surgery and Anaesthesia",
    blurb:
      "Plan and monitor anaesthesia, deliver IPPV, calculate drug volumes, chart dental findings, prepare a sterile theatre and produce diagnostic radiographs.",
    accent: "#0e7490",
    Icon: Activity,
    sims: ["anaesthesia", "anaesthesia2", "anaesmachine", "ippv", "drugcalc", "dental", "asepsis", "radiography", "anaescrisis"],
  },
  {
    id: "clinical-pathology",
    n: 3,
    title: "Clinical Pathology",
    blurb:
      "Sample handling, haematology, biochemistry and diagnostic interpretation from bench to report.",
    accent: "#b45309",
    Icon: Microscope,
    sims: ["clinpath", "urinalysis", "bloodwork"],
  },
  {
    id: "inpatient-care",
    n: 4,
    title: "Inpatient Care",
    blurb:
      "Fluid therapy, medication rounds, dispensing and infection control for the hospitalised patient.",
    accent: "#0f766e",
    Icon: Stethoscope,
    sims: ["fluidpump", "wardrounds", "dispensing", "drugstorage", "infection"],
  },
  {
    id: "nutrition-wellbeing",
    n: 5,
    title: "Nutrition and Wellbeing",
    blurb:
      "Body condition, energy requirements and feeding plans across life stages and clinical needs.",
    accent: "#15803d",
    Icon: Utensils,
    sims: ["nutrition"],
  },
  {
    id: "communication-practice",
    n: 6,
    title: "Communication and Professional Practice",
    blurb:
      "The human side of veterinary nursing — de-escalating difficult conversations, supporting grieving owners through euthanasia, and running a professional front desk.",
    accent: "#7c3aed",
    Icon: MessageSquareWarning,
    sims: ["conflict", "grief", "reception"],
  },
];

export const SECTION_BY_ID = Object.fromEntries(SECTIONS.map((s) => [s.id, s]));
